"use client"

import * as React from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  Phone,
  Mail,
  Clock,
  MapPin,
  ShoppingCart,
  ChevronDown,
  Home,
  Package,
  Truck,
  Info,
  Contact,
  Heart,
  Settings,
  User,
  LogOut,
  ClipboardList,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { useNavigationStore } from "@/store/use-navigation-store"
import { useCartStore } from "@/store/use-cart-store"
import { useWishlistStore } from "@/store/use-wishlist-store"
import { useCartAnimationStore } from "@/store/use-cart-animation-store"
import { useAuthStore } from "@/store/use-auth-store"
import { SearchAutocomplete } from "@/components/layout/SearchAutocomplete"
import { MegaMenu } from "@/components/layout/MegaMenu"
import { CurrencySelector } from "@/components/layout/CurrencySelector"
import { NotificationCenter } from "@/components/layout/NotificationCenter"
import { LoginDialog } from "@/components/auth/LoginDialog"
import { CONTACTS } from "@/lib/contacts"

const NAV_LINKS = [
  { label: "Производители", page: "manufacturers", icon: Info },
  { label: "Доставка", page: "delivery", icon: Truck },
  { label: "О нас", page: "about", icon: Info },
  { label: "Контакты", page: "contacts", icon: Contact },
] as const

/** Animated hamburger ↔ X icon */
function AnimatedHamburger({ open }: { open: boolean }) {
  return (
    <div className="relative flex size-5 items-center justify-center">
      <motion.span
        className="absolute h-[2px] w-5 rounded-full bg-current"
        animate={
          open
            ? { rotate: 45, y: 0 }
            : { rotate: 0, y: -6 }
        }
        transition={{ duration: 0.25, ease: "easeInOut" }}
      />
      <motion.span
        className="absolute h-[2px] w-5 rounded-full bg-current"
        animate={open ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
      />
      <motion.span
        className="absolute h-[2px] w-5 rounded-full bg-current"
        animate={
          open
            ? { rotate: -45, y: 0 }
            : { rotate: 0, y: 6 }
        }
        transition={{ duration: 0.25, ease: "easeInOut" }}
      />
    </div>
  )
}

/** Cart icon bounce animation */
function CartIconBounce() {
  const bounceKey = useCartAnimationStore((s) => s.cartBounceKey)
  return (
    <motion.div
      key={bounceKey}
      initial={{ scale: 1 }}
      animate={{ scale: [1, 1.3, 1] }}
      transition={{ duration: 0.35, ease: "easeInOut" }}
      className="absolute inset-0 flex items-center justify-center pointer-events-none"
    />
  )
}

/** Pulsing green dot indicator */
function PulsingDot() {
  return (
    <span className="relative flex size-2 shrink-0">
      <span className="absolute inline-flex size-full animate-ping rounded-full bg-green-500 opacity-75" />
      <span className="relative inline-flex size-2 rounded-full bg-green-500" />
    </span>
  )
}

export function Header() {
  const navigate = useNavigationStore((s) => s.navigate)
  const totalItems = useCartStore((s) => s.totalItems)
  const wishlistCount = useWishlistStore((s) => s.totalCount)
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const fetchMe = useAuthStore((s) => s.fetchMe)
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [scrolled, setScrolled] = React.useState(false)
  const [loginOpen, setLoginOpen] = React.useState(false)

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Fetch current user on mount
  React.useEffect(() => {
    fetchMe()
  }, [fetchMe])

  const cartCount = totalItems()
  const wishCount = wishlistCount()

  return (
    <header className={`sticky top-0 z-50 w-full bg-background/80 backdrop-blur-xl border-b border-border/50 transition-shadow ${scrolled ? "shadow-md" : ""}`}>
      {/* Top bar with gradient background */}
      <div className="border-b bg-gradient-to-r from-brand-dark/5 via-brand/5 to-brand-light/5">
        <div className="container mx-auto flex h-9 items-center justify-between px-4 text-xs text-muted-foreground">
          <div className="hidden items-center gap-4 md:flex">
            <a
              href={`tel:${CONTACTS.phoneRaw}`}
              className="flex items-center gap-1.5 hover:text-foreground transition-colors"
            >
              <Phone className="size-3" />
              <span className="font-medium">{CONTACTS.phoneLabel}</span>
            </a>
            <Separator orientation="vertical" className="h-4" />
            <a
              href={`tel:${CONTACTS.phone2Raw}`}
              className="flex items-center gap-1.5 hover:text-foreground transition-colors"
            >
              <Phone className="size-3" />
              <span className="font-medium">{CONTACTS.phone2}</span>
            </a>
            <Separator orientation="vertical" className="h-4" />
            <a
              href={`mailto:${CONTACTS.email}`}
              className="flex items-center gap-1.5 hover:text-foreground transition-colors"
            >
              <Mail className="size-3" />
              <span>{CONTACTS.email}</span>
            </a>
            <Separator orientation="vertical" className="h-4" />
            <span className="flex items-center gap-1.5">
              <PulsingDot />
              <span className="font-medium">Работаем сейчас</span>
              <span className="hidden lg:inline">· {CONTACTS.hours.weekdayRange}</span>
            </span>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <PulsingDot />
            <a
              href={`tel:${CONTACTS.phoneRaw}`}
              className="font-medium hover:text-foreground transition-colors"
            >
              {CONTACTS.phoneLabel}
            </a>
            <span className="text-muted-foreground">/</span>
            <a
              href={`tel:${CONTACTS.phone2Raw}`}
              className="font-medium hover:text-foreground transition-colors"
            >
              {CONTACTS.phone2}
            </a>
          </div>

          <div className="hidden md:flex items-center gap-1">
            <CurrencySelector />
            <NotificationCenter />
          </div>
          <div className="flex items-center gap-1.5">
            <MapPin className="size-3" />
            <span>Севастополь</span>
            <ChevronDown className="size-3" />
          </div>
        </div>
      </div>

      {/* Animated gradient border line */}
      <div className="h-[2px] gradient-line-animated" />

      {/* Main bar */}
      <div className="container mx-auto flex h-16 items-center justify-between gap-4 px-4">
        {/* Logo */}
        <button
          onClick={() => navigate("home")}
          className="shrink-0 flex items-center gap-2 logo-glow"
        >
          <div className="flex items-center justify-center size-8 rounded-lg bg-primary text-primary-foreground">
            <span className="text-sm font-black">Д</span>
          </div>
          <span className="text-xl font-bold tracking-tight">
            Двери<span className="text-primary">Ант</span>
          </span>
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden items-center gap-1 lg:flex">
          <MegaMenu />
          {NAV_LINKS.map((link) => (
            <Button
              key={link.page}
              variant="ghost"
              size="sm"
              onClick={() => navigate(link.page)}
              className="text-sm font-normal nav-link-underline"
            >
              {link.label}
            </Button>
          ))}
        </nav>

        {/* Search with autocomplete */}
        <div className="hidden max-w-sm flex-1 md:flex">
          <div className="search-expand w-40">
            <SearchAutocomplete />
          </div>
        </div>

        {/* Wishlist + Cart + Mobile Menu */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Wishlist */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("wishlist")}
            className="relative hidden sm:inline-flex"
            aria-label="Избранное"
          >
            <Heart className="size-5" />
            {wishCount > 0 && (
              <Badge className="absolute -top-1 -right-1 size-5 justify-center rounded-full p-0 text-[10px] bg-red-500 counter-animate">
                {wishCount}
              </Badge>
            )}
          </Button>

          {/* Cart */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("cart")}
            className="relative"
            aria-label="Корзина"
          >
            <CartIconBounce />
            <ShoppingCart className="size-5" data-cart-icon />
            {cartCount > 0 && (
              <Badge className="absolute -top-1 -right-1 size-5 justify-center rounded-full p-0 text-[10px] bg-primary cart-badge-pulse">
                {cartCount}
              </Badge>
            )}
          </Button>

          {/* Admin - only for admins */}
          {user?.isAdmin && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("admin")}
              className="hidden text-muted-foreground hover:text-foreground lg:inline-flex"
              aria-label="Админ-панель"
            >
              <Settings className="size-4" />
            </Button>
          )}

          {/* Auth: login button or user dropdown */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" aria-label="Профиль">
                  <Avatar className="size-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("profile")}>
                  <User className="mr-2 size-4" />
                  Мой профиль
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("profile")} className="data-[state=open]:bg-accent">
                  <ClipboardList className="mr-2 size-4" />
                  Мои заказы
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("recently-viewed")} className="data-[state=open]:bg-accent">
                  <Clock className="mr-2 size-4" />
                  История просмотров
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => logout()}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 size-4" />
                  Выйти
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLoginOpen(true)}
              className="hidden lg:inline-flex"
            >
              Войти
            </Button>
          )}

          {/* Mobile hamburger with animated icon */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                aria-label="Меню"
              >
                <AnimatedHamburger open={mobileOpen} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 flex flex-col">
              <SheetHeader>
                <SheetTitle className="text-left text-xl font-bold">
                  ДвериАнт
                </SheetTitle>
              </SheetHeader>

              {/* Mobile search */}
              <div className="mb-3 mt-2">
                <SearchAutocomplete />
              </div>

              {/* Mobile nav links with larger touch targets */}
              <nav className="flex flex-col gap-0.5 overflow-y-auto flex-1">
                <Button
                  variant="ghost"
                  className="justify-start gap-3 h-11 text-base"
                  onClick={() => { navigate("home"); setMobileOpen(false) }}
                >
                  <Home className="size-5" />
                  Главная
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start gap-3 h-11 text-base"
                  onClick={() => { navigate("catalog"); setMobileOpen(false) }}
                >
                  <Package className="size-5" />
                  Каталог
                </Button>
                {NAV_LINKS.map((link) => {
                  const Icon = link.icon
                  return (
                    <Button
                      key={link.page}
                      variant="ghost"
                      className="justify-start gap-3 h-11 text-base"
                      onClick={() => { navigate(link.page); setMobileOpen(false) }}
                    >
                      <Icon className="size-5" />
                      {link.label}
                    </Button>
                  )
                })}

                <Separator className="my-3" />

                <Button
                  variant="ghost"
                  className="justify-start gap-3 h-11 text-base"
                  onClick={() => { navigate("cart"); setMobileOpen(false) }}
                >
                  <div className="relative">
                    <ShoppingCart className="size-5" />
                    {cartCount > 0 && (
                      <span className="absolute -top-1.5 -right-2 flex size-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
                        {cartCount}
                      </span>
                    )}
                  </div>
                  Корзина
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start gap-3 h-11 text-base"
                  onClick={() => { navigate("wishlist"); setMobileOpen(false) }}
                >
                  <div className="relative">
                    <Heart className="size-5" />
                    {wishCount > 0 && (
                      <span className="absolute -top-1.5 -right-2 flex size-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
                        {wishCount}
                      </span>
                    )}
                  </div>
                  Избранное
                </Button>
                {user?.isAdmin && (
                  <Button
                    variant="ghost"
                    className="justify-start gap-3 h-11 text-base"
                    onClick={() => { navigate("admin"); setMobileOpen(false) }}
                  >
                    <Settings className="size-5" />
                    Админ-панель
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="justify-start gap-3 h-11 text-base"
                  onClick={() => { navigate("recently-viewed"); setMobileOpen(false) }}
                >
                  <Clock className="size-5" />
                  История просмотров
                </Button>

                <Separator className="my-3" />

                {/* Mobile auth section */}
                {user ? (
                  <>
                    <Button
                      variant="ghost"
                      className="justify-start gap-3 h-11 text-base"
                      onClick={() => { navigate("profile"); setMobileOpen(false) }}
                    >
                      <Avatar className="size-7">
                        <AvatarFallback className="bg-primary text-primary-foreground text-[10px] font-bold">
                          {user.name.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      {user.name}
                    </Button>
                    <Button
                      variant="ghost"
                      className="justify-start gap-3 h-11 text-base"
                      onClick={() => { navigate("profile"); setMobileOpen(false) }}
                    >
                      <ClipboardList className="size-5" />
                      Мои заказы
                    </Button>
                    <Button
                      variant="ghost"
                      className="justify-start gap-3 h-11 text-base text-destructive hover:text-destructive"
                      onClick={() => { logout(); setMobileOpen(false) }}
                    >
                      <LogOut className="size-5" />
                      Выйти
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    className="justify-start gap-3 h-11 text-base"
                    onClick={() => { setLoginOpen(true); setMobileOpen(false) }}
                  >
                    <User className="size-5" />
                    Войти
                  </Button>
                )}
              </nav>

              <Separator className="my-2" />
              <div className="flex items-center gap-2">
                <CurrencySelector />
                <NotificationCenter />
              </div>
              {/* Mobile contact section - prominent phone */}
              <div className="border-t pt-4 mt-auto">
                <a
                  href={`tel:${CONTACTS.phoneRaw}`}
                  className="flex items-center justify-center gap-2.5 rounded-xl bg-brand/10 px-4 py-3.5 text-base font-semibold text-brand transition-colors hover:bg-brand/15"
                >
                  <Phone className="size-5" />
                  {CONTACTS.phoneLabel}
                </a>
                <a
                  href={`tel:${CONTACTS.phone2Raw}`}
                  className="flex items-center justify-center gap-2.5 rounded-xl bg-brand/10 px-4 py-3.5 text-base font-semibold text-brand transition-colors hover:bg-brand/15 -mt-0.5"
                >
                  <Phone className="size-5" />
                  {CONTACTS.phone2}
                </a>
                <div className="mt-3 space-y-0.5 text-sm text-muted-foreground">
                  <a
                    href={`mailto:${CONTACTS.email}`}
                    className="flex items-center gap-2.5 py-2 hover:text-foreground transition-colors"
                  >
                    <Mail className="size-4" />
                    {CONTACTS.email}
                  </a>
                  <span className="flex items-center gap-2.5 py-2">
                    <Clock className="size-4" />
                    {CONTACTS.hours.weekdayRange}
                  </span>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Login Dialog */}
      <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
    </header>
  )
}
